// Persisted, user-defined arrangement of metric cards and comparison-plot
// blocks into ordered sections. Lives additively in the report view_config.
// When absent, the layout is derived from the current category grouping (the
// "default template"), so existing reports render unchanged.

import type { ComparisonPlotConfig } from "./comparisonPlot"
import { generateShortUUID } from "./id-generation/short-uuid"

// A section entry: a metric card or a comparison-plot block. Sections hold an
// ordered mix so plots can be interleaved among metric cards.
export type MetricLayoutItem =
  | { kind: "metric"; metricId: string }
  | { kind: "plot"; plot: ComparisonPlotConfig }

export interface MetricLayoutSection {
  id: string
  title: string
  items: MetricLayoutItem[]
}

export interface MetricLayoutConfig {
  sections: MetricLayoutSection[]
}

// Grouped metrics as RunMetricCards computes them: an ordered map of category
// display title -> metric ids in that category.
export type GroupedMetrics = Record<string, string[]>

export function metricItem(metricId: string): MetricLayoutItem {
  return { kind: "metric", metricId }
}

export function plotItem(plot: ComparisonPlotConfig): MetricLayoutItem {
  return { kind: "plot", plot }
}

// Stable drag/render key for a layout item.
export function metricLayoutItemId(item: MetricLayoutItem): string {
  return item.kind === "metric" ? item.metricId : item.plot.id
}

// Id for a user-created (non-category) section.
export function userSectionId(): string {
  return `user:${generateShortUUID()}`
}

// Append a plot block to the end of the last section, seeding a "Comparison"
// section when the layout has none.
export function addPlotToLayout(
  sections: MetricLayoutSection[],
  plot: ComparisonPlotConfig,
): MetricLayoutSection[] {
  if (sections.length === 0) {
    return [
      { id: userSectionId(), title: "Comparison", items: [plotItem(plot)] },
    ]
  }
  return sections.map((section, index) =>
    index === sections.length - 1
      ? { ...section, items: [...section.items, plotItem(plot)] }
      : section,
  )
}

// Replace a plot block's config wherever it lives.
export function updatePlotInLayout(
  sections: MetricLayoutSection[],
  plot: ComparisonPlotConfig,
): MetricLayoutSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) =>
      item.kind === "plot" && item.plot.id === plot.id ? plotItem(plot) : item,
    ),
  }))
}

// Remove a plot block by id.
export function removePlotFromLayout(
  sections: MetricLayoutSection[],
  plotId: string,
): MetricLayoutSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => !(item.kind === "plot" && item.plot.id === plotId),
    ),
  }))
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section"
  )
}

// Deterministic id for an auto-derived (category) section so re-derivation is
// stable across loads. User-created sections get their own generated ids.
function categorySectionId(title: string): string {
  return `cat:${slugify(title)}`
}

// Tolerate the legacy metricIds-only section shape (layouts saved before plot
// blocks existed) by mapping bare metric ids to metric items.
function normalizeSection(section: MetricLayoutSection): MetricLayoutSection {
  if (Array.isArray(section.items)) {
    return section
  }
  const legacyIds = (section as { metricIds?: string[] }).metricIds ?? []
  return {
    id: section.id,
    title: section.title,
    items: legacyIds.map(metricItem),
  }
}

// The default template: today's category grouping, in its current order.
export function getDefaultMetricLayout(
  groupedMetrics: GroupedMetrics,
): MetricLayoutConfig {
  return {
    sections: Object.entries(groupedMetrics).map(([title, metricIds]) => ({
      id: categorySectionId(title),
      title,
      items: metricIds.map(metricItem),
    })),
  }
}

// Resolve the sections to actually render: honor the saved order, drop metrics
// no longer present, and append metrics that appeared since the layout was
// saved to their default-category section (creating it if needed). Plot blocks
// and user-created sections are preserved (even when empty).
export function applyMetricLayout(
  saved: MetricLayoutConfig | undefined,
  groupedMetrics: GroupedMetrics,
): MetricLayoutSection[] {
  if (!saved || saved.sections.length === 0) {
    return getDefaultMetricLayout(groupedMetrics).sections
  }

  const categoryByMetricId = new Map<string, string>()
  for (const [title, ids] of Object.entries(groupedMetrics)) {
    for (const id of ids) {
      categoryByMetricId.set(id, title)
    }
  }

  const placedMetrics = new Set<string>()
  const placedPlots = new Set<string>()
  const sections: MetricLayoutSection[] = saved.sections.map((rawSection) => {
    const section = normalizeSection(rawSection)
    const items = section.items.filter((item) => {
      if (item.kind === "plot") {
        if (placedPlots.has(item.plot.id)) {
          return false
        }
        placedPlots.add(item.plot.id)
        return true
      }
      if (
        !categoryByMetricId.has(item.metricId) ||
        placedMetrics.has(item.metricId)
      ) {
        return false
      }
      placedMetrics.add(item.metricId)
      return true
    })
    return { ...section, items }
  })

  // Metrics present now but absent from the saved layout (newly added) — append
  // to their default category section, preserving the grouped order.
  for (const id of categoryByMetricId.keys()) {
    if (placedMetrics.has(id)) {
      continue
    }
    const category = categoryByMetricId.get(id) as string
    // Match the category's section by its deterministic id, not its title, so a
    // renamed section still receives its newly-added metrics instead of
    // spawning a duplicate section.
    const sectionId = categorySectionId(category)
    let section = sections.find((candidate) => candidate.id === sectionId)
    if (!section) {
      section = { id: sectionId, title: category, items: [] }
      sections.push(section)
    }
    section.items.push(metricItem(id))
    placedMetrics.add(id)
  }

  return sections
}
